import { AlertTriangle } from "lucide-react";

import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { Spinner } from "@src/shared/components/ui/spinner";

export interface LoginCallbackPageV2Props {
  error?: string | null;
  errorDescription?: string | null;
  isLoading?: boolean;
  onRetry?: () => void;
}

export function LoginCallbackPageV2({
  error,
  errorDescription,
  isLoading = false,
  onRetry,
}: LoginCallbackPageV2Props) {
  const hasError = !!error;

  return (
    <VerticalFlexbox
      as="main"
      alignItems="center"
      justifyContent="center"
      gap="md"
      className="w-full max-w-[420px] px-[30px] py-11 text-center text-foreground"
    >
      {isLoading && !hasError ? (
        <>
          <div className="flex size-[46px] items-center justify-center rounded-[13px] bg-accent text-primary">
            <Spinner size="sm" />
          </div>
          <HeaderText
            tag="h1"
            className="max-w-[420px] text-balance text-[25px] leading-tight tracking-[-0.6px]"
          >
            Completing sign in
          </HeaderText>
          <Text
            as="p"
            appearance="subdued"
            sizing="sm"
            className="max-w-[350px] text-balance leading-[1.55]"
          >
            Please wait while we verify your credentials.
          </Text>
        </>
      ) : null}

      {hasError ? (
        <>
          <VerticalFlexbox
            alignItems="center"
            justifyContent="center"
            className="size-[46px] rounded-[13px] bg-destructive/10 text-destructive"
          >
            <AlertTriangle className="size-[22px]" aria-hidden="true" />
          </VerticalFlexbox>
          <HeaderText
            tag="h1"
            className="max-w-[420px] text-balance text-[25px] leading-tight tracking-[-0.6px]"
          >
            {getErrorTitle(error)}
          </HeaderText>
          <Text
            as="p"
            appearance="subdued"
            sizing="sm"
            className="max-w-[350px] text-balance leading-[1.55]"
          >
            {formatErrorDescription(errorDescription, error)}
          </Text>
          {onRetry ? (
            <Button
              type="button"
              className="mt-2 h-[42px] rounded-[9px] text-[13.5px] font-semibold"
              onClick={onRetry}
            >
              Try again
            </Button>
          ) : null}
        </>
      ) : null}
    </VerticalFlexbox>
  );
}

function getErrorTitle(error: string | null | undefined): string {
  switch (error) {
    case "access_denied":
      return "Access denied";
    case "invalid_request":
      return "Invalid request";
    case "unauthorized_client":
      return "Unauthorized";
    case "unsupported_response_type":
      return "Configuration error";
    case "invalid_scope":
      return "Invalid scope";
    case "server_error":
      return "Server error";
    case "temporarily_unavailable":
      return "Temporarily unavailable";
    default:
      return "Authentication failed";
  }
}

function formatErrorDescription(
  description: string | null | undefined,
  error: string | null | undefined,
): string {
  if (description) {
    return description;
  }

  switch (error) {
    case "access_denied":
      return "You do not have permission to access this application. Please contact your administrator.";
    case "invalid_request":
      return "The authentication request was invalid. Please try signing in again.";
    case "unauthorized_client":
      return "This application is not authorized to perform this action.";
    default:
      return "An error occurred during authentication. Please try signing in again.";
  }
}
